import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { SUPPORTED_LANGS, type AppLang, normalizeLang, chooseLanguage } from "@/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const LABELS: Record<AppLang, string> = {
  es: "Español",
  en: "English",
  fr: "Français",
  pt: "Português",
  it: "Italiano",
  de: "Deutsch",
};

const FLAGS: Record<AppLang, string> = {
  es: "🇲🇽",
  en: "🇺🇸",
  fr: "🇫🇷",
  pt: "🇧🇷",
  it: "🇮🇹",
  de: "🇩🇪",
};

interface Props {
  variant?: "compact" | "full";
}

export function LanguageSwitcher({ variant = "compact" }: Props) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = normalizeLang(i18n.language);

  // Close popover on scroll so it never floats over other UI while the page moves.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", close, { capture: true } as never);
  }, [open]);

  const change = async (lng: AppLang) => {
    setOpen(false);
    if (lng === current) return;
    // Persist BEFORE switching so any code that reads storage on the next
    // render picks the new value.
    await chooseLanguage(lng);
  };


  if (variant === "full") {
    // Segmented sliding pill — used in the mobile menu.
    const idx = SUPPORTED_LANGS.indexOf(current);
    const pct = 100 / SUPPORTED_LANGS.length;
    return (
      <div className="space-y-2">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t("common.language")}
        </span>
        <div className="relative grid grid-cols-6 rounded-full border border-border bg-muted/50 p-1">
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-1 rounded-full bg-primary shadow-[0_6px_16px_-6px_oklch(0.72_0.18_45/0.55)] transition-all duration-300 ease-out"
            style={{ width: `calc(${pct}% - 0.25rem)`, left: `calc(${idx * pct}% + 0.125rem)` }}
          />
          {SUPPORTED_LANGS.map((lng) => (
            <button
              key={lng}
              type="button"
              onClick={() => change(lng)}
              className={`relative z-10 flex flex-col items-center gap-0.5 rounded-full py-1.5 text-[11px] font-semibold transition-colors ${
                current === lng ? "text-primary-foreground" : "text-foreground/70 hover:text-foreground"
              }`}
              aria-pressed={current === lng}
            >
              <span className="text-base leading-none">{FLAGS[lng]}</span>
              <span className="uppercase tracking-wider">{lng}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("common.language")}
          className="inline-flex h-10 items-center gap-1.5 rounded-full border border-amber-200/30 bg-black/40 px-3 text-sm font-semibold text-amber-100 shadow-sm transition-all hover:border-amber-200/60 hover:bg-black/60"
        >
          <Globe className="h-4 w-4 text-amber-300" />
          <span className="text-base leading-none">{FLAGS[current]}</span>
          <span className="hidden uppercase tracking-wider sm:inline">{current}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 text-amber-200/70 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={10}
        avoidCollisions={true}
        collisionPadding={16}
        className="w-60 overflow-hidden rounded-2xl border border-amber-200/15 bg-[#0a0e14]/95 p-0 text-amber-50 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl lg:!w-60"
      >
        <div className="border-b border-amber-200/10 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200/70">
          {t("common.language")}
        </div>
        <ul role="listbox" className="p-1.5">
          {SUPPORTED_LANGS.map((lng) => {
            const active = current === lng;
            return (
              <li key={lng}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => change(lng)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                    active
                      ? "bg-amber-400/15 font-semibold text-amber-200"
                      : "text-amber-50/85 hover:bg-white/5 hover:text-amber-100"
                  }`}
                >
                  <span className="text-lg leading-none">{FLAGS[lng]}</span>
                  <span className="flex-1">{LABELS[lng]}</span>
                  {active && <Check className="h-4 w-4 text-amber-300" />}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
